/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Skeleton, Button, Dialog, IconButton, CircularProgress } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import AppButton from 'Shared/UI/Button';
import StatusBadge from 'Shared/UI/StatusBadge';
import noImage from 'Images/no_image.svg';
import { formatAmount } from 'Utils/ulilities';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useUser from 'hooks/useUser';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import toast from 'Utils/toast';
import { downloadFacturePdf, getFactureDetails, getFactureById } from './FacturesServices';
import { getReglementDocument } from '../Reglements/ReglementServices';
import iconEye from 'Images/Icon_eye.svg';

const TYPE_LABELS = { CHEQUE: 'Chèque', TRAITE: 'Traite', VIREMENT: 'Virement', LCN: 'LCN', ESPECE: 'Espèce' };

const PdfPreviewModal = ({ url, title, onClose, onDownload }) => (
  <Dialog
    open={!!url}
    onClose={onClose}
    maxWidth={false}
    sx={{
      '& .MuiDialog-paper': {
        width: '900px',
        maxWidth: '85vw',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0px 12px 40px rgba(0,0,0,0.18)',
      },
    }}
    slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 37, 59, 0.28)' } } }}
  >
    <Box sx={{ backgroundColor: '#FFFFFF', p: '10px' }}>
      <Box sx={{
        width: '100%',
        height: '560px',
        backgroundColor: 'rgba(131, 151, 174, 0.21)',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: '12px', left: '12px', zIndex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#385576', backgroundColor: 'rgba(255,255,255,0.85)', px: '10px', py: '4px', borderRadius: '6px' }}>{title}</Typography>
          <Button
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={onDownload}
            sx={{ height: '40px', px: '14px', borderRadius: '8px', backgroundColor: '#FFFFFF', color: '#385576', textTransform: 'none', fontWeight: 600, fontSize: '13px', boxShadow: '0px 2px 6px rgba(0,0,0,0.08)', border: '1px solid #DEE5EE', '&:hover': { backgroundColor: '#F8FAFC' } }}
          >
            Télécharger
          </Button>
        </Box>
        <Box sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}>
          <IconButton size="small" onClick={onClose} sx={{ color: '#B0BCC9', width: 28, height: 28, '&:hover': { color: '#385576', backgroundColor: 'rgba(255,255,255,0.6)' } }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
        {url && url.split('?')[0].toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)
          ? <img src={url} alt={title} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          : <iframe src={`${url}#view=Fit`} title={title} width="100%" height="100%" style={{ border: 'none', display: 'block' }} />
        }
      </Box>
    </Box>
  </Dialog>
);

const fmt = (v) => formatAmount(v || 0);

const GratuiteChip = () => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    px: '8px', height: '20px', borderRadius: '5px', ml: '6px',
    backgroundColor: '#D3FDA7',
  }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00B894', lineHeight: 1, textTransform: 'uppercase' }}>
      Gratuit
    </Typography>
  </Box>
);

const formatDateFull = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return format(d, 'dd/MM/yyyy', { locale: fr });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return format(d, 'dd/MM/yyyy');
};

const FactureDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { callApi } = useNetworkLoader();
  const isMF = useUser();

  const [facture, setFacture] = useState(location.state?.facture || null);
  const [factureLoading, setFactureLoading] = useState(!location.state?.facture);
  const [details, setDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [docLoading, setDocLoading] = useState({});
  const [downloadingFacture, setDownloadingFacture] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!facture) {
      setFactureLoading(true);
      callApi(
        () => getFactureById(id),
        (data) => {
          setFacture(data);
          setFactureLoading(false);
        },
      );
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setDetails([]);
    setDetailsLoading(true);
    callApi(
      () => getFactureDetails(id),
      (data) => {
        setDetails(data || []);
        setDetailsLoading(false);
      },
    );
  }, [id]);

  const reglements = facture?.reglements || [];

  const triggerBlobDownload = (url, filename) => {
    const ext = url.split('?')[0].split('.').pop() || 'jpg';
    const name = `${filename}.${ext}`;
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = name;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Erreur lors du téléchargement'));
  };

  const fetchDocUrl = (reg, mode) => {
    if (!reg.id || docLoading[`${reg.id}-${mode}`]) return;
    setDocLoading((prev) => ({ ...prev, [`${reg.id}-${mode}`]: true }));
    callApi(
      () => getReglementDocument(reg.id),
      (data) => {
        setDocLoading((prev) => ({ ...prev, [`${reg.id}-${mode}`]: false }));
        if (!data?.fileName) { toast.error('Document introuvable'); return; }
        if (mode === 'view') {
          setPreview({ url: data.fileName, title: `${TYPE_LABELS[reg.type?.toUpperCase()] || reg.type || 'Document'}: ${reg.reference}` });
        } else {
          triggerBlobDownload(data.fileName, reg.reference || 'document');
        }
      },
    );
  };

  const handleDownload = () => {
    if (!facture?.reference || downloadingFacture) return;
    setDownloadingFacture(true);
    callApi(
      () => downloadFacturePdf(facture.reference),
      (blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${facture.reference}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        setDownloadingFacture(false);
      },
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px', fontFamily: "'Open Sans', sans-serif", '& *': { fontFamily: "'Open Sans', sans-serif" } }}>

      <PdfPreviewModal
        url={preview?.url}
        title={preview?.title}
        onClose={() => setPreview(null)}
        onDownload={() => {
          if (!preview?.url) return;
          triggerBlobDownload(preview.url, preview.title || 'document');
        }}
      />

      {/** Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
        <Box>
          <Box onClick={() => history.goBack()} sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', mb: '8px' }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 11, color: '#8397AE' }} />
            <Typography sx={{ fontSize: '14px', color: '#8397AE', fontWeight: 600 }}>{isMF ? 'Retour aux devis' : 'Retour aux factures'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '2px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#385576' }}>
              {isMF ? 'Détails du devis' : 'Détails de la facture'}:{' '}
              {factureLoading
                ? <Skeleton component="span" variant="text" width={140} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
                : <span style={{ color: '#000000', fontWeight: 700 }}>{facture?.reference}</span>
              }
            </Typography>
            {!factureLoading && facture && <StatusBadge status={facture.statut} isMF={isMF} />}
          </Box>
          <Typography sx={{ fontSize: '16px', color: '#385576', fontWeight: 600 }}>
            Montant total net TTC:{' '}
            {factureLoading
              ? <Skeleton component="span" variant="text" width={100} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
              : (
                <>
                  <span style={{ color: '#007BC6', fontWeight: 700 }}>{fmt(facture?.amount)} DH</span>
                  {facture?.montantRestant > 0 && !isMF && (
                    <>
                      <span style={{ color: '#707070', fontWeight: 400 }}> = </span>
                      <span style={{ color: '#0AD69D', fontWeight: 700 }}>Réglé: {fmt(facture.montantRegle)}</span>
                      <span style={{ color: '#707070', fontWeight: 400 }}> + </span>
                      <span style={{ color: '#FEA26F', fontWeight: 700 }}>Restant: {fmt(facture.montantRestant)}</span>
                    </>
                  )}
                </>
              )
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
          <AppButton
            variant="custom"
            label={isMF ? 'Télécharger devis' : 'Télécharger facture'}
            icon={<FileDownloadOutlinedIcon />}
            color="#007BC6"
            hoverColor="#005F9E"
            textColor="#fff"
            onClick={handleDownload}
            loading={downloadingFacture}
          />
        </Box>
      </Box>

      {/** Main content */}
      <Grid container spacing={0} sx={{ flexWrap: 'nowrap', gap: '0px', ml: '2px' }}>

        {/** Left: Articles table */}
        <Grid item sx={{ flex: 1, minWidth: 60 }}>
          <Box sx={{ mb: '15px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
              Articles ({detailsLoading ? <Skeleton component="span" variant="text" width={20} sx={{ display: 'inline-block', verticalAlign: 'middle' }} /> : details.length})
            </Typography>
          </Box>
          <Box sx={{ backgroundColor: '#FFFFFF', border: '1.5px solid #D1D5DB', borderRadius: '8px', overflow: 'hidden', opacity: 1, boxShadow: '0px 2px 8px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr 1fr 1fr 1.5fr', px: '20px', height: '40px', alignItems: 'center', backgroundColor: '#FFFFFF' }}>
              {['PRODUITS', 'PRIX UNITAIRE BRUT TTC', 'QUANTITÉ', 'REMISE', 'TOTAL NET TTC'].map((col) => (
                <Typography key={col} sx={{ fontSize: '13px', fontWeight: 600, color: '#385576', textTransform: 'uppercase' }}>
                  {col}
                </Typography>
              ))}
            </Box>
            {detailsLoading ? (
              [0, 1, 2, 3].map((i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr 1fr 1fr 1.5fr', px: '20px', py: '6px', borderTop: '1.5px solid #D1D5DB', alignItems: 'center', backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', height: '38px' }}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Box>
                      <Skeleton variant="text" width={120} height={18} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width={70} height={18} />
                  <Skeleton variant="text" width={30} height={18} />
                  <Skeleton variant="text" width={40} height={18} />
                  <Skeleton variant="text" width={80} height={18} />
                </Box>
              ))
            ) : details.length === 0 ? (
              <Box sx={{ px: '20px', py: '20px', borderTop: '1.5px solid #D1D5DB', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '14px', color: '#8397AE' }}>Aucun article</Typography>
              </Box>
            ) : details.map((detail, index) => (
              <Box
                key={detail.id || index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1.5fr 1fr 1fr 1.5fr',
                  px: '20px',
                  py: '10px',
                  borderTop: '1.5px solid #D1D5DB',
                  borderLeft: detail.gratuite ? '4px solid #00B894' : 'none',
                  alignItems: 'center',
                  backgroundColor: detail.gratuite ? '#EAFBF3' : (index % 2 === 0 ? '#FFFFFF' : '#F8FAFC'),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box sx={{ width: 36, height: 36, flexShrink: 0, backgroundColor: '#e0e4e9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {detail.product?.image
                      ? <img src={detail.product.image} alt="product" style={{ width: 36, height: 36, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                      : null}
                    <img src={noImage} alt="product" style={{ width: 36, height: 36, objectFit: 'contain', display: detail.product?.image ? 'none' : 'block' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#000000' }}>
                      {detail.product?.name || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '14px', color: '#8397AE' }}>
                        RÉF: {detail.product?.reference || '—'}
                      </Typography>
                      {detail.gratuite && <GratuiteChip />}
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: detail.gratuite ? '#C2C2CA' : '#000000',
                  textDecoration: detail.gratuite ? 'line-through' : 'none',
                }}
                >
                  {fmt(detail.prixUnitaire)}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>{detail.quantity ?? '—'}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: detail.gratuite ? '#00B894' : '#000000' }}>
                  {detail.remise != null ? `${detail.remise}%` : '—'}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: detail.gratuite ? '#00B894' : '#1A2035' }}>
                  {fmt(detail.montantTtc)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/** Right: Informations + Règlements */}
        <Grid item sx={{ width: '460px', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', ml: '20px', mr: '-6px', alignItems: 'stretch' }}>

            {/** Informations card */}
            <Box sx={{ backgroundColor: '#F8FAFC', p: '4px' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>Informations</Typography>
              {[
                { label: isMF ? "Date du devis" : "Date d'émission", value: factureLoading ? null : formatDateFull(facture?.date) },
                ...(!isMF ? [{ label: "Date d'échéance", value: factureLoading ? null : formatDateFull(facture?.dateEcheance) }] : []),
              ].map((item, i, arr) => (
                <Box key={i} sx={{ backgroundColor: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '8px', p: '8px', pl: '16px', mb: i < arr.length - 1 ? '6px' : 0, minHeight: '70px', boxShadow: '0px 2px 6px rgba(0,0,0,0.05)' }}>
                  <Typography sx={{ fontSize: '14px', color: '#8398AE', mb: '0px', pt: '8px' }}>{item.label}</Typography>
                  {item.value === null
                    ? <Skeleton variant="text" width={200} height={20} />
                    : <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#385576', textTransform: 'capitalize' }}>{item.value}</Typography>
                  }
                </Box>
              ))}
            </Box>

            {/** Documents attachés card — hidden for MF */}
            {!isMF && <Box sx={{ backgroundColor: '#F8FAFC', p: '4px' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>Documents attachés ({reglements.length})</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {factureLoading ? (
                  [0, 1].map((i) => (
                    <Box key={i} sx={{ backgroundColor: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '8px', p: '16px', boxShadow: '0px 2px 6px rgba(0,0,0,0.05)' }}>
                      <Skeleton variant="text" width={160} height={20} sx={{ mb: '6px' }} />
                      <Skeleton variant="text" width={240} height={16} />
                    </Box>
                  ))
                ) : reglements.length === 0 ? (
                  <Box sx={{ backgroundColor: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '8px', p: '16px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '14px', color: '#8397AE' }}>Aucun document attaché</Typography>
                  </Box>
                ) : reglements.map((reg, i) => (
                  <Box key={i} sx={{ backgroundColor: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '8px', p: '16px', boxShadow: '0px 2px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#000000', mb: '4px' }}>
                        {reg.type ? `${TYPE_LABELS[reg.type.toUpperCase()] || reg.type}: ` : ''}{reg.reference || '—'}
                        {reg.impaye && <span style={{ color: '#EF456E', fontWeight: 600, marginLeft: '8px' }}>Impayé</span>}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#385576' }}>
                        {formatDateShort(reg.rgDate)}
                        <span style={{ color: '#8398AE', fontWeight: 600 }}> - Montant Attaché: </span>
                        <span style={{ color: '#027BC6', fontWeight: 700 }}>{fmt(reg.amount)} Dhs</span>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, ml: '12px' }}>
                      {docLoading[`${reg.id}-view`]
                        ? <CircularProgress size={18} sx={{ color: '#007BC6' }} />
                        : <img src={iconEye} alt="voir" style={{ width: 20, height: 20, objectFit: 'contain', cursor: 'pointer', opacity: 0.7 }} onClick={() => fetchDocUrl(reg, 'view')} />
                      }
                      {docLoading[`${reg.id}-download`]
                        ? <CircularProgress size={18} sx={{ color: '#007BC6' }} />
                        : <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: '#A0AEC0', cursor: 'pointer', '&:hover': { color: '#007BC6' } }} onClick={() => fetchDocUrl(reg, 'download')} />
                      }
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>}

          </Box>
        </Grid>

      </Grid>

    </Box>
  );
};

export default FactureDetail;
